// src/utils/path-utils.ts - Browser-Safe Path Utilities (StackBlitz Compatible)

/**
 * Browser-safe path utilities (replaces Node.js 'path' module)
 * StackBlitz compatible version with proper absolute path handling
 */
export const pathUtils = {
    /**
     * Get directory name from file path
     */
    dirname: (filePath: string): string => {
        // Normalize path separators
        const normalized = filePath.replace(/\\/g, '/');
        const parts = normalized.split('/');

        // Remove file name
        parts.pop();

        // Return joined path or '.' for empty
        const result = parts.join('/');

        // Preserve absolute path indicator
        if (normalized.startsWith('/')) {
            return result || '/';
        }

        return result || '.';
    },

    /**
     * Resolve relative path against base path
     */
    resolve: (base: string, relative: string): string => {
        // Normalize separators
        base = base.replace(/\\/g, '/');
        relative = relative.replace(/\\/g, '/');

        // Handle absolute paths
        if (relative.startsWith('/')) {
            return relative;
        }

        // Get base directory
        const baseDir = pathUtils.dirname(base);

        // Handle current directory reference
        if (relative.startsWith('./')) {
            relative = relative.slice(2);
        }

        // Split paths
        const baseParts = baseDir.split('/').filter(p => p !== '' && p !== '.');
        const relativeParts = relative.split('/').filter(p => p !== '');

        // Start with base parts
        const result = [...baseParts];

        // Process relative parts
        for (const part of relativeParts) {
            if (part === '..') {
                if (result.length > 0) {
                    result.pop();
                }
            } else if (part !== '.') {
                result.push(part);
            }
        }

        // Join and ensure absolute path if base was absolute
        const joined = result.join('/');

        // Preserve absolute path indicator
        if (base.startsWith('/') || baseDir.startsWith('/')) {
            return '/' + joined;
        }

        return joined || '.';
    },

    /**
     * Get base name (file name) from file path
     */
    basename: (filePath: string): string => {
        return filePath.replace(/\\/g, '/').split('/').pop() || filePath;
    },

    /**
     * Get file extension
     */
    extname: (filePath: string): string => {
        const basename = pathUtils.basename(filePath);
        const dotIndex = basename.lastIndexOf('.');
        return dotIndex > 0 ? basename.slice(dotIndex) : '';
    },

    /**
     * Check if path is absolute
     */
    isAbsolute: (filePath: string): boolean => {
        return filePath.startsWith('/') || /^[a-zA-Z]:/.test(filePath);
    },

    /**
     * Normalize path - remove redundant separators and resolve . and ..
     */
    normalize: (filePath: string): string => {
        const normalized = filePath.replace(/\\/g, '/');
        const parts = normalized.split('/').filter(p => p !== '');
        const result: string[] = [];

        for (const part of parts) {
            if (part === '..') {
                if (result.length > 0 && result[result.length - 1] !== '..') {
                    result.pop();
                } else if (!normalized.startsWith('/')) {
                    result.push(part);
                }
            } else if (part !== '.') {
                result.push(part);
            }
        }

        const joined = result.join('/');
        return normalized.startsWith('/') ? '/' + joined : joined;
    }
};